<script setup lang="ts">
import { Spinner } from "@/components/ui/spinner";
import { RefreshCwIcon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import WordmarkLogo from "./WordmarkLogo.vue";
import { isNativePlatform } from "../capacitor/platform";
import { useTheme } from "../queries/theme";
import {
  batteryOptimizationPromptVisible,
  acceptBatteryOptimizationPrompt,
  dismissBatteryOptimizationPrompt,
} from "../capacitor/battery-optimization";

const { t } = useI18n();

const portalTheme = useTheme();
const theme = computed(() => portalTheme.accent);
const highContrast = computed(() => portalTheme.highAccessibility);

// CSS variable holding the user's currently selected accent colour as
// HSL components, e.g. "255 94.2% 66.5%". Inline styles wrap it with
// hsl(var(...) / alpha) so we can render the icon and its container in
// the brand colour without needing per-theme Tailwind classes.
const accentVar = computed(() => `--theme-${theme.value ?? "grape"}${highContrast.value ? "-hc" : ""}`);
const iconContainerStyle = computed(() => ({
  backgroundColor: `hsl(var(${accentVar.value}) / 0.12)`,
  borderColor: `hsl(var(${accentVar.value}) / 0.4)`,
}));
const iconStyle = computed(() => ({
  color: `hsl(var(${accentVar.value}))`,
}));
const allowButtonClass = computed(() => `theme-${theme.value ?? "grape"}${highContrast.value ? "-hc" : ""}`);

const busy = ref(false);

const visible = computed(() => isNativePlatform() && batteryOptimizationPromptVisible.value);

async function handleAllow() {
  if (busy.value) return;
  busy.value = true;
  try {
    await acceptBatteryOptimizationPrompt();
  } finally {
    busy.value = false;
  }
}

function handleNotNow() {
  if (busy.value) return;
  dismissBatteryOptimizationPrompt();
}
</script>

<template>
  <!--
    Teleport to body so the overlay sits outside #app. HeadlessUI Dialog's
    useInert marks the #app container inert when any dialog is open (e.g.
    LoginView's biometric-save prompt, which fires concurrently with this
    overlay right after login). Without the teleport, the overlay's buttons
    become non-interactive while that dialog is on screen.
  -->
  <Teleport to="body">
    <Transition name="battery-opt-fade">
      <div
        v-if="visible"
        class="fixed inset-0 z-95 flex min-h-full flex-1 flex-col justify-center bg-background pt-24 sm:px-6 lg:px-8"
      >
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
          <WordmarkLogo class="mx-auto dark:opacity-50" />
        </div>

        <div class="mt-10 flex flex-col items-center gap-6 sm:mx-auto sm:w-full sm:max-w-sm">
          <div class="flex flex-col items-center gap-4 px-4">
            <div :style="iconContainerStyle" class="flex h-14 w-14 items-center justify-center rounded-full border">
              <RefreshCwIcon :style="iconStyle" class="h-7 w-7" />
            </div>

            <h1 class="text-center text-xl font-semibold text-foreground">
              {{ t("msg.batteryOptRequired") }}
            </h1>

            <p class="text-center text-sm text-muted-foreground">
              {{ t("msg.batteryOptRequiredDescription") }}
            </p>
          </div>

          <div class="w-full space-y-3 px-4">
            <button
              @click="handleAllow"
              :disabled="busy"
              :class="allowButtonClass"
              class="flex w-full items-center justify-center rounded-md px-3 py-2.5 text-sm font-semibold text-primary-foreground shadow-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Spinner v-if="busy" class="mr-2" />
              {{ t("msg.batteryOptAllow") }}
            </button>
            <button
              @click="handleNotNow"
              :disabled="busy"
              class="flex w-full items-center justify-center rounded-md border border-border bg-background px-3 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              {{ t("msg.batteryOptNotNow") }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.battery-opt-fade-enter-active,
.battery-opt-fade-leave-active {
  transition: opacity 0.3s ease;
}
.battery-opt-fade-enter-from,
.battery-opt-fade-leave-to {
  opacity: 0;
}
</style>
